import {
  type BrandVariants,
  createLightTheme,
  createDarkTheme,
  type Theme,
} from "@fluentui/react-components";

// ecocloud green ramp (cream/green editorial design system). The landing's
// accent #0e6a34 sits at shade 80 — Fluent's default `colorBrandBackground`
// slot — so brand-tinted Fluent internals (links, focus rings, selected
// states, Avatar initials) all pick up the green family automatically.
export const ecoGreen: BrandVariants = {
  10: "#04120a",
  20: "#07200f",
  30: "#092d15",
  40: "#0a3a1c",
  50: "#0b4722",
  60: "#0c5428",
  70: "#0d5f2e",
  80: "#0e6a34", // ← the landing accent (--c-gold)
  90: "#157a3a",
  100: "#2e8f4f",
  110: "#4aa465",
  120: "#68b97d",
  130: "#89cd97",
  140: "#aadfb3",
  150: "#ccefd1",
  160: "#e9f9ea",
};

// Neutral surfaces stay transparent so the cream page / white .glass panels
// own the paint (Fluent components sit ON our surfaces). Hover/pressed states
// use the green-cream wash from the panel kit.
const editorialLightOverrides = {
  // Primary actions are the landing's lime pill: lime fill + forest ink.
  // (CSS can't tell Fluent button appearances apart — Griffel class names
  // carry no "primary" marker — so the color MUST come from the theme.)
  colorBrandBackground: "#9fe870",
  colorBrandBackgroundHover: "#83dd4c",
  colorBrandBackgroundPressed: "#83dd4c",
  colorBrandBackgroundSelected: "#83dd4c",
  colorNeutralForegroundOnBrand: "#163300",
  colorNeutralBackground1: "transparent",
  colorNeutralBackground1Hover: "rgba(19, 46, 10, 0.05)",
  colorNeutralBackground1Pressed: "rgba(19, 46, 10, 0.08)",
  colorNeutralBackground1Selected: "rgba(19, 46, 10, 0.07)",
  colorNeutralBackground2: "transparent",
  colorNeutralBackground3: "#f0f4e6",
  colorSubtleBackground: "transparent",
  colorSubtleBackgroundHover: "rgba(19, 46, 10, 0.06)",
  colorNeutralForeground1: "#132e0a",
  colorNeutralForeground2: "#4e6146",
  colorNeutralForeground3: "#73826a",
  colorNeutralStroke1: "#d9dfc9",
  colorNeutralStroke2: "#e8eadd",
  colorNeutralStroke3: "#eef0e3",
  fontFamilyBase:
    'var(--font-inter), Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  fontFamilyMonospace:
    'var(--font-lp-mono), "SF Mono", ui-monospace, Consolas, monospace',
} as const;

const editorialDarkOverrides = {
  // Same lime CTA in the forest theme — exactly like the landing's dark bands.
  colorBrandBackground: "#9fe870",
  colorBrandBackgroundHover: "#b9ee92",
  colorBrandBackgroundPressed: "#b9ee92",
  colorBrandBackgroundSelected: "#b9ee92",
  colorNeutralForegroundOnBrand: "#163300",
  colorNeutralBackground1: "transparent",
  colorNeutralBackground1Hover: "rgba(238, 246, 231, 0.06)",
  colorNeutralBackground1Pressed: "rgba(238, 246, 231, 0.09)",
  colorNeutralBackground1Selected: "rgba(238, 246, 231, 0.08)",
  colorNeutralBackground2: "transparent",
  colorNeutralBackground3: "rgba(255, 255, 255, 0.07)",
  colorSubtleBackground: "transparent",
  colorSubtleBackgroundHover: "rgba(238, 246, 231, 0.08)",
  colorNeutralForeground1: "#eef6e7",
  colorNeutralForeground2: "rgba(238, 246, 231, 0.72)",
  colorNeutralForeground3: "rgba(238, 246, 231, 0.55)",
  colorNeutralStroke1: "rgba(238, 246, 231, 0.2)",
  colorNeutralStroke2: "rgba(238, 246, 231, 0.13)",
  colorNeutralStroke3: "rgba(238, 246, 231, 0.08)",
  // Forest context: links & brand foregrounds go lime (like .lp-forest).
  colorBrandForeground1: "#9fe870",
  colorBrandForeground2: "#b9ee92",
  colorBrandForegroundLink: "#9fe870",
  colorBrandForegroundLinkHover: "#b9ee92",
  colorCompoundBrandStroke: "#9fe870",
  colorCompoundBrandForeground1: "#9fe870",
  colorCompoundBrandBackground: "#9fe870",
  fontFamilyBase:
    'var(--font-inter), Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  fontFamilyMonospace:
    'var(--font-lp-mono), "SF Mono", ui-monospace, Consolas, monospace',
} as const;

export const lightTheme: Theme = {
  ...createLightTheme(ecoGreen),
  ...editorialLightOverrides,
};

export const darkTheme: Theme = {
  ...createDarkTheme(ecoGreen),
  ...editorialDarkOverrides,
};
